import { useEffect, useState } from 'react';

import type { DocumentNode, OperationVariables, QueryHookOptions, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client';

import localStorageEx from 'bundles/common/utils/localStorageEx';

const CACHE_PREFIX = 'gql_cache:';
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours

type CacheEntry<TData> = {
  data: TData;
  timestamp: number;
};

type UseCachedQueryOptions<TData, TVariables extends OperationVariables> = QueryHookOptions<TData, TVariables> & {
  /** A stable string that fully determines cache identity. Change it to invalidate. */
  cacheKey: string;
  /** Time-to-live in milliseconds. Defaults to 24 hours. */
  ttl?: number;
  /** Whether localStorage caching is enabled. Defaults to true. When false, always delegates to useQuery. */
  cacheEnabled?: boolean;
};

function readCache<TData>(key: string, ttl: number): TData | null {
  const entry = localStorageEx.getItem<CacheEntry<TData> | null>(key, JSON.parse, null);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttl) {
    localStorageEx.removeItem(key);
    return null;
  }
  return entry.data;
}

function writeCache<TData>(key: string, data: TData): void {
  const entry: CacheEntry<TData> = { data, timestamp: Date.now() };
  localStorageEx.setItem(key, entry, JSON.stringify);
}

/**
 * A thin wrapper around Apollo's `useQuery` that adds localStorage-based TTL caching.
 *
 * On mount, checks localStorage for a cached response. If the cache is fresh (within `ttl`),
 * returns the cached data immediately and skips the network request. On a cache miss or
 * expiry, delegates to `useQuery` as normal and persists the result for future visits.
 *
 * Degrades gracefully when localStorage is unavailable (SSR, private browsing, quota).
 */
function useCachedQuery<TData, TVariables extends OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: UseCachedQueryOptions<TData, TVariables>
) {
  const { ttl = DEFAULT_TTL, cacheKey, cacheEnabled = true, ...queryOptions } = options;

  const storageKey = cacheKey ? `${CACHE_PREFIX}${cacheKey}` : null;

  const [cachedData, setCachedData] = useState<TData | null>(null);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [prevStorageKey, setPrevStorageKey] = useState(storageKey);
  const [prevCacheEnabled, setPrevCacheEnabled] = useState(cacheEnabled);
  const [prevSkip, setPrevSkip] = useState(queryOptions.skip);

  // Reset state synchronously during render when key/config changes (React's
  // "adjusting state based on props" pattern — prevents stale data from the
  // old key from ever reaching the DOM).
  if (prevStorageKey !== storageKey || prevCacheEnabled !== cacheEnabled || prevSkip !== queryOptions.skip) {
    setPrevStorageKey(storageKey);
    setPrevCacheEnabled(cacheEnabled);
    setPrevSkip(queryOptions.skip);
    setCachedData(null);
    setCacheChecked(false);
  }

  // Read cache in an effect — localStorage access belongs in effects, not render.
  useEffect(() => {
    if (storageKey && cacheEnabled && !queryOptions.skip) {
      const cached = readCache<TData>(storageKey, ttl);
      setCachedData(cached);
    } else {
      setCachedData(null);
    }
    setCacheChecked(true);
  }, [storageKey, cacheEnabled, queryOptions.skip, ttl]);

  const hasCachedData = cacheEnabled && cachedData !== null;

  const { loading, error, data } = useQuery<TData, TVariables>(query, {
    ...queryOptions,
    skip: queryOptions.skip || (cacheEnabled && (hasCachedData || !cacheChecked)),
  });

  // Persist fresh data to localStorage
  useEffect(() => {
    if (storageKey && cacheEnabled && data) {
      writeCache(storageKey, data);
      setCachedData(data);
    }
  }, [data, storageKey, cacheEnabled]);

  return {
    data: data ?? cachedData ?? undefined,
    loading: !cacheChecked || (!hasCachedData && loading),
    error: hasCachedData ? undefined : error,
  };
}

export default useCachedQuery;
